import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import BusinessCategoryModel from '../../../core/models/business-category.model';
import { BusinessCategoryService } from '../../../core/services/business-category.service';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";

declare var $: any;

/**
 * @component BusinessCategoryComponent
 * @description
 * This component manages the "Business Category" master data within the Admin Panel > User Access section.
 * It provides a comprehensive interface for Administrators to Create, Read, Update, and Delete (CRUD)
 * business category definitions.
 *
 * **Key Responsibilities:**
 * 1. **Data Visualization**: Displays a paginated/infinite-scrolled list of existing business categories.
 * 2. **Data Management**: Provides a Reactive Form for adding new categories or editing existing ones.
 * 3. **Validation**: Enforces strict validation rules (min/max length, patterns) on category names and codes.
 * 4. **Duplicate Prevention**: Implements client-side logic to detect and warn about duplicate category names before submission.
 * 5. **Search & Filtering**: Allows real-time filtering of the category list based on name or code.
 *
 * **Data Flow:**
 * - On initialization (`ngOnInit`), the component fetches all business categories via `BusinessCategoryService`.
 * - The raw data is stored in `businessCategories`.
 * - A `BatchLoader` utility is initialized to handle client-side pagination (infinite scroll) to optimize rendering performance.
 * - The UI binds to `businessCategoriesList`, which contains the currently loaded subset of data.
 * - When searching, `filteredBusinessCategories` is populated, and the `BatchLoader` switches context to render from this filtered source.
 *
 * **User Roles & Permissions:**
 * - This component is typically restricted to users with **Super Admin** or **System Admin** roles.
 * - Standard users generally consume this data in dropdowns elsewhere but do not modify it here.
 *
 * **UI Features:**
 * - **Infinite Scroll**: Automatically loads more items as the user scrolls down.
 * - **Debounced Search**: Search input waits for user to stop typing before filtering.
 * - **Responsive Form**: The form layout adapts based on the number of fields (handled via `formFieldCount`).
 *
 * @selector app-business-category
 * @implements OnInit, OnDestroy
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Component({
  selector: 'app-business-category',
  templateUrl: './business-category.component.html',
  styleUrls: ['./business-category.component.scss']
})
export class BusinessCategoryComponent implements OnInit, OnDestroy {
  // ==========================================================================
  // DATA PROPERTIES
  // ==========================================================================

  /**
   * @type {BusinessCategoryModel[]}
   * @description Master list of all business categories fetched from the backend.
   * @default []
   */
  public businessCategories: BusinessCategoryModel[] = [];

  /**
   * @type {BusinessCategoryModel[]}
   * @description The subset of business categories currently displayed in the UI.
   * This list grows as the user scrolls down (infinite scroll implementation).
   * @default []
   */
  public businessCategoriesList: BusinessCategoryModel[] = [];

  /**
   * @type {BusinessCategoryModel}
   * @description The model used for binding to the Add/Edit form.
   * Represents the category currently being created or modified.
   */
  public businessCategory: BusinessCategoryModel = new BusinessCategoryModel();

  /**
   * @type {BusinessCategoryModel[]}
   * @description Stores the results of a search query.
   * Used by the BatchLoader when `isFiltering` is true.
   * @default []
   */
  public filteredBusinessCategories: BusinessCategoryModel[] = [];

  /**
   * @type {string}
   * @description The current search string entered by the user.
   */
  public search: string;

  // ==========================================================================
  // FORM & STATE PROPERTIES
  // ==========================================================================

  /**
   * @type {boolean}
   * @description Flag indicating if the form has been submitted.
   * Used to trigger validation error messages in the UI.
   * @default false
   */
  public submitted = false;

  /**
   * @type {FormGroup}
   * @description Reactive form group for adding/updating business categories.
   * Contains controls for `name`, `code`, and `description`.
   */
  public addBusinessCategoryForm: FormGroup;

  /**
   * @type {boolean}
   * @description Flag to indicate if the form is in "Create" mode.
   */
  public submitButton: boolean;

  /**
   * @type {boolean}
   * @description Flag to indicate if the form is in "Update" mode.
   */
  public updateButton: boolean;

  /**
   * @type {any}
   * @description Stores the original name of the category being edited.
   * Used to check if the name has changed during duplicate validation.
   */
  public originalBusinessCategories: any;

  /**
   * @type {boolean}
   * @description Prevents double-submission of the form by locking the submit action.
   */
  public businessCategoryDoubleClick: boolean;

  // ==========================================================================
  // UI STATE PROPERTIES
  // ==========================================================================

  /**
   * @type {boolean}
   * @description Flag to display a "No Data" message if the list is empty.
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * @type {number}
   * @description Tracks the current active page number (if pagination were used).
   */
  public activePageNo: number;

  /**
   * @type {boolean}
   * @description Flag to trigger clearing of the search input field.
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * @type {boolean}
   * @description Controls the visibility of the main page loader/spinner.
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * @type {boolean}
   * @description Controls the visibility of the list-specific loader (e.g., during infinite scroll).
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * @type {number}
   * @description Tracks the starting index of the loaded data batch.
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * @type {boolean}
   * @description Indicates if the user is currently filtering the list via search.
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * @type {boolean}
   * @description Flag indicating that the filtered result set is empty.
   * @default false
   */
  public noList: boolean = false;

  /** @private @type {any} @description Timeout reference for scroll event throttling. */
  private scrollTimeout: any;
  /** @type {any} @description Timeout reference for search input debouncing. */
  searchTimeout: any;
  /** @type {number} @description Count of form fields to adjust UI layout dynamically. @default 0 */
  public formFieldCount: number = 0;
  /** @type {boolean} @description Toggles the visibility of the Add/Edit form. @default false */
  public showForm: boolean = false;

  /**
   * @method constructor
   * @description Initializes the component, injects dependencies, and builds the reactive form.
   *
   * @param {ToastrService} toasterService - @service For displaying success/error notifications.
   * @param {Sidenavaction} sidenavaction - @service For controlling the sidebar navigation state.
   * @param {FormBuilder} formBuilder - @service For creating the reactive form group.
   * @param {BusinessCategoryService} businessCategoryService - @service For API operations.
   * @param {CommonMethodsService} commonMethodsService - @service For common utility methods (alerts).
   * @param {LocationStrategy} locationStrategy - @service For interacting with the URL path.
   * @param {PreviousRouteService} previousRouteService - @service For tracking navigation history.
   */
  constructor(public toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public businessCategoryService: BusinessCategoryService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.businessCategory = new BusinessCategoryModel();
    /*form validation*/
    this.addBusinessCategoryForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      code: ['', [Validators.minLength(3), Validators.maxLength(10), Validators.pattern('^[A-Za-z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.addBusinessCategoryForm.controls).length;
  }

  /**
   * @private
   * @type {BatchLoader<any>}
   * @description Utility instance for handling client-side pagination/infinite scroll.
   */
  private batchLoader: BatchLoader<any>;

  /**
   * @method ngOnInit
   * @lifecycle
   * @async
   * @description Component initialization lifecycle hook.
   * 1. Resets state and scrolls to top.
   * 2. Sets up navigation history.
   * 3. Fetches all business categories from the backend.
   * 4. Formats date fields for display.
   * 5. Initializes the `BatchLoader` for infinite scrolling.
   *
   * @throws {Error} Captures and displays errors via `CommonMethodsService` if data fetch fails.
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.businessCategories = [];
    this.businessCategoriesList = [];
    this.filteredBusinessCategories = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.sidenavaction.x_navigation();
    try {
      this.businessCategories = await this.businessCategoryService.getAllBusinessCategories();
      if (this.businessCategories.length > 0) {
        this.businessCategories.forEach(eachBusinessCategory => {
          eachBusinessCategory.updatedAt = JSON.parse(JSON.stringify(moment(eachBusinessCategory.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for the window scroll event.
   * Triggers `checkScrollAndLoad` to determine if more data needs to be loaded.
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks if the user has scrolled near the bottom of the page.
   * If the threshold is reached and no loading is in progress, it triggers the `batchLoader`
   * to load the next set of records.
   *
   * @side-effect Modifies `businessCategoriesList` via the BatchLoader callback.
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Configures the `BatchLoader` utility.
   * Defines the source of data (filtered vs full list) and the callback to update the UI list.
   *
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredBusinessCategories : this.businessCategories,
      (updatedList) => {
        this.businessCategoriesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  //To cancel ongoing requests and unsubscribe from subscriptions.
  /**
   * @method ngOnDestroy
   * @lifecycle
   * @description Cleanup lifecycle hook.
   * Cancels any pending HTTP requests in the service and destroys the batch loader instance
   * to prevent memory leaks.
   */
  public ngOnDestroy(): void {
    this.businessCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  // soil type register form convenience getter for easy access to form fields
  /**
   * @method businessCategoryFormValidation
   * @description Getter for easy access to form controls in the HTML template.
   * Used for displaying validation errors.
   * @returns {AbstractControl} The form controls of `addBusinessCategoryForm`.
   */
  public get businessCategoryFormValidation() {
    return this.addBusinessCategoryForm.controls;
  }

  // search functionality
  /**
   * @method applySearch
   * @async
   * @description Filters the business category list based on the `search` string.
   * - Matches against `name` or `code`.
   * - Resets the `BatchLoader` to display the filtered results from the beginning.
   *
   * @returns {Promise<void>}
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.businessCategories.filter(category =>
        category.name?.toLowerCase().includes(query) ||
        category.code?.toLowerCase().includes(query)
      )
      : this.businessCategories;
    this.filteredBusinessCategories = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.businessCategoriesList = [];
      return;
    }
    await this.batchLoader?.loadNextBatch(true);
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * @method onSearchChanged
   * @description Debounces the search input to prevent excessive filtering operations.
   * Waits 300ms after the last keystroke before calling `applySearch`.
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * @method onCategoryChange
   * @description Checks for duplicate business category names during data entry.
   *
   * **Logic:**
   * - **Create Mode**: Checks if the entered name exists in `businessCategories`. If yes, alerts and clears input.
   * - **Update Mode**: Checks if the entered name exists in other records (excluding current ID).
   * - Supports "fuzzy" matching warnings (using `indexOf`) with user confirmation.
   *
   * @param {string} category - The category name entered by the user.
   * @side-effect May clear `businessCategory.name` or show browser alerts/toasts.
   */
  public onCategoryChange(category) {
    if (this.submitButton == true) {
      for (let i = 0; i < this.businessCategories.length; i++) {
        let obj = this.businessCategories[i];
        let histCulturecategory = obj.name.toUpperCase();
        if (histCulturecategory == category.toUpperCase()) {
          this.toasterService.info('This is already existed please enter other  business category');
          this.businessCategory.name = '';
          break;
        } else if (histCulturecategory.toUpperCase().indexOf(category.toUpperCase()) != -1 && category != '') {
          let booleanval = confirm('Similar name is there do you want to enter');
          if (!booleanval) {
            this.businessCategory.name = '';
          }
          break;
        }
      }
    } else if (this.updateButton == true) {
      this.originalBusinessCategories = _.filter(this.businessCategories, { 'id': (this.businessCategory.id) })[0].name;
      for (let i = 0; i < this.businessCategories.length; i++) {
        let obj = this.businessCategories[i];
        let histCulturecategory = obj.name.toUpperCase();
        if (histCulturecategory == category.toUpperCase() && obj.id != this.businessCategory.id) {
          this.toasterService.info('Business category already existed please enter other business category', '', {
            timeOut: 10000
          });
          this.businessCategory.name = '';
          this.businessCategory.name = _.filter(this.businessCategories, { 'id': (this.businessCategory.id) })[0].name;
          break;
        } else if (category.toUpperCase().indexOf(histCulturecategory) != -1 && category.toUpperCase() != this.originalBusinessCategories.toUpperCase() && category != '') {
          var booleanval = confirm('similar category is there do you want to enter');
          if (!booleanval) {
            this.businessCategory.name = _.filter(this.businessCategories, { 'id': (this.businessCategory.id) })[0].name;
          }
          break;
        }
      }
    }

  }

  /*Add soilType*/
  /**
   * @method addUpdateBusinessCategory
   * @async
   * @description Handles the form submission for both creating and updating business categories.
   * 1. Validates the form.
   * 2. Calls `create` or `updateOneByID` on the service based on the mode.
   * 3. Refreshes the list via `ngOnInit` upon success.
   *
   * @returns {Promise<void>}
   * @throws {Error} Displays error via `CommonMethodsService` if the API call fails.
   */
  public async addUpdateBusinessCategory() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.addBusinessCategoryForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.addBusinessCategoryForm.value; // contains updated values
    try {
      this.businessCategoryDoubleClick = true;
      if (this.submitButton) {
        await this.businessCategoryService.create(formData);
        this.toasterService.success('Business category created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateBusinessCategory = { ...formData };
        delete updateBusinessCategory.id;
        await this.businessCategoryService.updateOneByID(this.businessCategory.id, updateBusinessCategory);
        this.toasterService.success('Business category updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.businessCategoryDoubleClick = false;
    }
  }

  /*Get soil type by id*/
  /**
   * @method getBusinessCategoryById
   * @description Prepares the UI for editing a specific business category.
   * - Finds the category by ID.
   * - Populates the form with existing data.
   * - Switches the view to "Update" mode.
   * @param {string} id - The ID of the business category to edit.
   */
  public getBusinessCategoryById(id: string) {
    this.businessCategoryDoubleClick = false;
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    const foundBusinessCategory = this.businessCategoriesList.find(uom => uom?.id === id);
    this.businessCategory = foundBusinessCategory ? { ...foundBusinessCategory } : new BusinessCategoryModel(); // clone object
    if (this.businessCategory) {
      this.addBusinessCategoryForm.patchValue({
        name: this.businessCategory.name,
        code: this.businessCategory.code,
        description: this.businessCategory.description
      });
    }
  }

  /**
   * @method clear
   * @description Resets the form and component state to default values.
   * Clears validation errors, resets flags, and hides the form.
   */
  public clear() {
    this.addBusinessCategoryForm.reset();
    this.businessCategory = new BusinessCategoryModel();
    this.businessCategoryDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.showForm = false;
  }

  /**
   * @method openForm
   * @description Opens the form in "Create" mode.
   * Sets the `submitButton` flag to true and recalculates form field counts for layout.
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.addBusinessCategoryForm.controls).length;
  }
}
